import React from "react";
import { Link } from "react-router-dom";
import Logo from "../../assets/gallery/Logo2.png"; // ✅ add your logo

/**
 * FooterPro – GVSI Clean & Care (DE)
 * - Modal-ready legal links via onOpenLegal('datenschutz'|'agb'|'widerruf'|'impressum')
 * - Falls back to React Router <Link> if onOpenLegal is not provided
 * - Tailwind-only (no external CSS)
 */

export default function FooterPro({
  company = {
    name: "GVSI Clean & Care",
    street: "Rosenheim",
    zip: "83024",
    city: "Deutschland",
    country: "Deutschland",
    phone: "+49 160 7635410‬",
    email: "info@gvsiclean.de",
  },
  onContactClick, // optional: smooth-scroll hook for contact link
  onOpenLegal,    // optional: opens modal overlay with slug
}) {
  const address = `${company.street}, ${company.zip} ${company.city}`;

  return (
    <footer className="relative isolate bg-gray-50 border-t border-gray-100">
     

      <div className="mx-auto max-w-6xl px-6 lg:px-12 py-14">
        <div className="grid gap-10 md:grid-cols-12">
          {/* Brand & value props */}
          <div className="md:col-span-5 lg:col-span-4">
            <div className="flex items-center gap-3">
              <a href="/" aria-label="Go to homepage" className="shrink-0">
                <img
                  src={Logo}
                  alt="GVSI Clean & Care logo"
                  className="h-10 w-auto object-contain"
                  loading="lazy"
                  decoding="async"
                />
              </a>
              <div>
                <p className="text-base font-extrabold tracking-tight text-gray-900">
                  GVSI Clean & Care
                </p>
              </div>
            </div>

            <p className="mt-4 max-w-md text-sm leading-6 text-gray-600">
              Zuverlässig, gründlich und termintreu – wir bringen Räume zum Glänzen. Fair, transparent und nachhaltig.
            </p>

            {/* Trust row */}
            <ul className="mt-6 flex flex-wrap gap-3 text-xs text-gray-700">
              <li className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1.5 shadow-sm">
                <ShieldIcon className="h-4 w-4 text-emerald-600" /> Versichert & haftpflichtgedeckt
              </li>
              <li className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1.5 shadow-sm">
                <LeafIcon className="h-4 w-4 text-emerald-600" /> Umweltbewusste Mittel
              </li>
              <li className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1.5 shadow-sm">
                <ClockIcon className="h-4 w-4 text-emerald-600" /> Pünktlich & zuverlässig
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div className="md:col-span-4 lg:col-span-3">
            <h4 className="text-sm font-semibold tracking-wide text-gray-900">Kontakt</h4>
            <address className="mt-3 not-italic text-sm text-gray-700">
              <div className="flex gap-3">
                <MapPinIcon className="mt-0.5 h-4 w-4 text-gray-400" /> <span>{address}</span>
              </div>
              <div className="mt-2 flex gap-3">
                <PhoneIcon className="mt-0.5 h-4 w-4 text-gray-400" />
                <a className="hover:text-emerald-700" href={`tel:${company.phone.replace(/\s+/g, "")}`}>
                  {company.phone}
                </a>
              </div>
              <div className="mt-2 flex gap-3">
                <MailIcon className="mt-0.5 h-4 w-4 text-gray-400" />
                <a className="hover:text-emerald-700" href={`mailto:${company.email}`}>
                  {company.email}
                </a>
              </div>
            </address>
            <div className="mt-4 flex items-center gap-3">
              <a
                href="#contact"
                onClick={onContactClick}
                className="inline-flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-100"
              >
                <ChatIcon className="h-4 w-4" /> Termin anfragen
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div className="md:col-span-3 lg:col-span-2">
            <h4 className="text-sm font-semibold tracking-wide text-gray-900">Schnellzugriff</h4>
            <ul className="mt-3 space-y-2 text-sm text-gray-700">
              <li><a className="hover:text-emerald-700" href="#home">Home</a></li>
              <li><a className="hover:text-emerald-700" href="#services">Leistungen</a></li>
              <li><a className="hover:text-emerald-700" href="#about">Über uns</a></li>
              <li><a className="hover:text-emerald-700" href="#gallery">Galerie</a></li>
              <li><a className="hover:text-emerald-700" href="#contact" onClick={onContactClick}>Kontakt</a></li>
            </ul>
          </div>

          {/* Legal */}
          <div className="md:col-span-12 lg:col-span-3">
            <h4 className="text-sm font-semibold tracking-wide text-gray-900">Rechtliches</h4>
            <ul className="mt-3 space-y-2 text-sm text-gray-700">
              <li><LegalItem label="Impressum" slug="impressum" onOpenLegal={onOpenLegal} to="/impressum" /></li>
              <li><LegalItem label="Privacy Policy" slug="datenschutz" onOpenLegal={onOpenLegal} to="/datenschutz" /></li>
              <li><LegalItem label="Terms & Conditions (AGB)" slug="agb" onOpenLegal={onOpenLegal} to="/agb" /></li>
              <li><LegalItem label="Right of Withdrawal" slug="widerruf" onOpenLegal={onOpenLegal} to="/widerruf" /></li>
              <li><a className="hover:text-emerald-700" href="/cookies">Cookie-Einstellungen</a></li>
              <li><a className="hover:text-emerald-700" href="/barrierefreiheit">Erklärung zur Barrierefreiheit</a></li>
            </ul>
          </div>
        </div>

        {/* Divider */}
        <div className="my-8 h-px bg-gray-100" />

{/* Bottom bar */}
<div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
  {/* Left: copyright */}
  <p className="text-xs text-gray-500 text-center md:text-left">
    © {new Date().getFullYear()} {company.name}. Alle Rechte vorbehalten.
  </p>

  {/* Right: powered by with logo */}
  <div className="flex items-center justify-center md:justify-end gap-2">
    <span className="text-xs text-gray-500">Powered by</span>
    <a
      href="https://gjyshidigital.com"
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1"
    >
      <img
        src={require("../../assets/gallery/Gjyshi.png")}
        alt="GJYSHI DIGITAL Logo"
        className="h-10 w-auto object-contain"
      />
    </a>
  </div>
</div>
      </div>  
    </footer>
  );
}

/** Button to open modal if onOpenLegal is provided; otherwise <Link> */
function LegalItem({ label, slug, onOpenLegal, to }) {
  if (typeof onOpenLegal === "function") {
    return (
      <button
        type="button"
        onClick={() => onOpenLegal(slug)}
        className="hover:text-emerald-700"
      >
        {label}
      </button>
    );
  }
  return (
    <Link className="hover:text-emerald-700" to={to}>
      {label}
    </Link>
  );
}

/* Icons */
function ShieldIcon({ className = "" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M12 3l8 3v6c0 5-3.5 8-8 10-4.5-2-8-5-8-10V6l8-3z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  );
}
function LeafIcon({ className = "" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M5 21c7 0 14-7 14-14 0 0-7-2-14 5 0 0 5 0 7-2" />
    </svg>
  );
}
function ClockIcon({ className = "" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 3" />
    </svg>
  );
}
function MapPinIcon({ className = "" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M12 21s-6-5.2-6-10a6 6 0 1112 0c0 4.8-6 10-6 10z" />
      <circle cx="12" cy="11" r="2.5" />
    </svg>
  );
}
function PhoneIcon({ className = "" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M22 16.9v3a2 2 0 01-2.18 2c-3.2-.4-6.2-1.6-8.63-3.07A19.5 19.5 0 013.16 9.81 19.8 19.8 0 01.98 2.72 2 2 0 013.06 1H6a2 2 0 012 1.72c.12.9.33 1.78.63 2.63a2 2 0 01-.45 2.11L7.1 7.1a16 16 0 008.48 8.48l.93-1.08a2 2 0 012.11-.45c.85.3 1.73.51 2.63.63A2 2 0 0122 16.9z" />
    </svg>
  );
}
function MailIcon({ className = "" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M4 6h16v12H4z" />
      <path d="M22 6l-10 7L2 6" />
    </svg>
  );
}
function ChatIcon({ className = "" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M21 15a4 4 0 01-4 4H8l-5 3V7a4 4 0 014-4h10a4 4 0 014 4v8z" />
    </svg>
  );
}

